import {useMemo} from 'react'
import {getCurrentUserLogin} from '@github-ui/client-env'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {useThreads} from '../../../../../stores/layout-selectors'
import {ThreadGroupVariant, classifyUnresolvedThread, type UnresolvedThreadGroupVariant} from '../util/classify-thread'

export type UnresolvedThreadGroups = Record<UnresolvedThreadGroupVariant, Thread[]>

function createEmptyBuckets(): UnresolvedThreadGroups {
  return {
    [ThreadGroupVariant.Commented]: [],
    [ThreadGroupVariant.Replied]: [],
    [ThreadGroupVariant.Mentioned]: [],
    [ThreadGroupVariant.YouCommented]: [],
    [ThreadGroupVariant.YouReplied]: [],
    [ThreadGroupVariant.Pending]: [],
  }
}

function useUnresolvedThreads(): Thread[] {
  const threads = useThreads()
  return useMemo(() => threads.filter(thread => !thread.isResolved), [threads])
}

/**
 * Groups every unresolved thread from {@link useThreads} into exactly one
 * {@link UnresolvedThreadGroupVariant} bucket. Classification (including viewer mention detection) is
 * delegated to {@link classifyUnresolvedThread}; resolved threads are excluded.
 */
export function useUnresolvedThreadGroups(): UnresolvedThreadGroups {
  const threads = useUnresolvedThreads()
  const viewerLogin = getCurrentUserLogin()

  return useMemo(() => {
    const buckets = createEmptyBuckets()

    for (const thread of threads) {
      const bucket = classifyUnresolvedThread(thread, viewerLogin)
      buckets[bucket].push(thread)
    }

    return buckets
  }, [threads, viewerLogin])
}
